import { useEffect } from 'react'

interface SEOProps {
  title: string
  description: string
  canonical?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogUrl?: string
  noindex?: boolean
}

const BASE_URL = 'https://umnik.ai'
const DEFAULT_OG_IMAGE = `${BASE_URL}/og/ogbanner.jpg`

function setMeta(name: string, content: string, attribute: string = 'name') {
  let el = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attribute, name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

export default function useSEO({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  noindex,
}: SEOProps) {
  useEffect(() => {
    document.title = title

    setMeta('description', description)

    setMeta('og:title', ogTitle || title, 'property')
    setMeta('og:description', ogDescription || description, 'property')
    setMeta('og:image', ogImage || DEFAULT_OG_IMAGE, 'property')
    setMeta('og:url', ogUrl || canonical || BASE_URL, 'property')
    setMeta('og:type', 'website', 'property')
    setMeta('og:site_name', 'Umnik.AI', 'property')
    setMeta('og:locale', 'ru_RU', 'property')
    setMeta('og:image:width', '1200', 'property')
    setMeta('og:image:height', '630', 'property')

    setMeta('twitter:card', 'summary_large_image')
    setMeta('twitter:title', ogTitle || title)
    setMeta('twitter:description', ogDescription || description)
    setMeta('twitter:image', ogImage || DEFAULT_OG_IMAGE)

    if (noindex) {
      setMeta('robots', 'noindex, nofollow')
    } else {
      const robotsEl = document.querySelector('meta[name="robots"]')
      if (robotsEl) robotsEl.remove()
    }

    if (canonical) {
      setLink('canonical', canonical)
    } else {
      // Без canonical в пропсах — снимаем унаследованный от прошлого роута:
      // SPA-переходы иначе оставляют чужой canonical (стейл-мета).
      document.querySelector('link[rel="canonical"]')?.remove()
    }
  }, [title, description, canonical, ogTitle, ogDescription, ogImage, ogUrl, noindex])
}